/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import moment from 'moment';
import noop from 'lodash/noop';

import { DEFAULT_LANGUAGE, DEFAULT_LOCALE, SUPPORTED_LANGUAGES } from '../../constants/constants';

export const getSupportedLanguage = userLanguages => {
  // Check preference list for direct match or locale match
  let supportedLanguage =
    userLanguages.find(
      userLanguage =>
        SUPPORTED_LANGUAGES.includes(userLanguage) ||
        SUPPORTED_LANGUAGES.includes(userLanguage.split('-')[0])
    ) || DEFAULT_LANGUAGE;

  supportedLanguage = SUPPORTED_LANGUAGES.includes(supportedLanguage)
    ? supportedLanguage
    : supportedLanguage.split('-')[0];

  return supportedLanguage.toLowerCase();
};

export const getSupportedLocale = userLanguages =>
  userLanguages
    ? userLanguages.find(
        userLanguage =>
          SUPPORTED_LANGUAGES.includes(userLanguage) ||
          SUPPORTED_LANGUAGES.includes(userLanguage.split('-')[0])
      ) || DEFAULT_LOCALE
    : DEFAULT_LOCALE;

export const handleError = error =>
  console.error(`An error occurred while loading translations: ${error}`);

// returns a promise
export const loadMessages = language => {
  switch (language) {
    case 'cs':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-cs", webpackMode: "lazy" */
            `../../../lang/cs.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-cs", webpackMode: "lazy" */
            `../../../lang/mock/cs.json`
          ).catch(handleError);
    case 'da':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-da", webpackMode: "lazy" */
            `../../../lang/da.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-da", webpackMode: "lazy" */
            `../../../lang/mock/da.json`
          ).catch(handleError);
    case 'de':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-de", webpackMode: "lazy" */
            `../../../lang/de.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-de", webpackMode: "lazy" */
            `../../../lang/mock/de.json`
          ).catch(handleError);
    case 'es':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-es", webpackMode: "lazy" */
            `../../../lang/es.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-es", webpackMode: "lazy" */
            `../../../lang/mock/es.json`
          ).catch(handleError);
    case 'fi':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-fi", webpackMode: "lazy" */
            `../../../lang/fi.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-fi", webpackMode: "lazy" */
            `../../../lang/mock/fi.json`
          ).catch(handleError);
    case 'fr':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-fr", webpackMode: "lazy" */
            `../../../lang/fr.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-fr", webpackMode: "lazy" */
            `../../../lang/mock/fr.json`
          ).catch(handleError);
    case 'hr':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-hr", webpackMode: "lazy" */
            `../../../lang/hr.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-hr", webpackMode: "lazy" */
            `../../../lang/mock/hr.json`
          ).catch(handleError);
    case 'hu':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-hu", webpackMode: "lazy" */
            `../../../lang/hu.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-hu", webpackMode: "lazy" */
            `../../../lang/mock/hu.json`
          ).catch(handleError);
    case 'it':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-it", webpackMode: "lazy" */
            `../../../lang/it.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-it", webpackMode: "lazy" */
            `../../../lang/mock/it.json`
          ).catch(handleError);
    case 'ja':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-ja", webpackMode: "lazy" */
            `../../../lang/ja.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-ja", webpackMode: "lazy" */
            `../../../lang/mock/ja.json`
          ).catch(handleError);
    case 'ko':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-ko", webpackMode: "lazy" */
            `../../../lang/ko.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-ko", webpackMode: "lazy" */
            `../../../lang/mock/ko.json`
          ).catch(handleError);
    case 'nl':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-nl", webpackMode: "lazy" */
            `../../../lang/nl.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-nl", webpackMode: "lazy" */
            `../../../lang/mock/nl.json`
          ).catch(handleError);
    case 'nb': // Norwegian is actually nb in the browser FYI: https://github.com/moment/moment/issues/1495
    case 'no':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-nb", webpackMode: "lazy" */
            `../../../lang/nb.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-nb", webpackMode: "lazy" */
            `../../../lang/mock/nb.json`
          ).catch(handleError);
    case 'pl':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-pl", webpackMode: "lazy" */
            `../../../lang/pl.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-pl", webpackMode: "lazy" */
            `../../../lang/mock/pl.json`
          ).catch(handleError);
    case 'pt-br':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-pt-br", webpackMode: "lazy" */
            `../../../lang/pt-br.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-pt-br", webpackMode: "lazy" */
            `../../../lang/mock/pt-br.json`
          ).catch(handleError);
    case 'sk':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-sk", webpackMode: "lazy" */
            `../../../lang/sk.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-sk", webpackMode: "lazy" */
            `../../../lang/mock/sk.json`
          ).catch(handleError);
    case 'sl':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-sl", webpackMode: "lazy" */
            `../../../lang/sl.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-sl", webpackMode: "lazy" */
            `../../../lang/mock/sl.json`
          ).catch(handleError);
    case 'sv':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-sv", webpackMode: "lazy" */
            `../../../lang/sv.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-sv", webpackMode: "lazy" */
            `../../../lang/mock/sv.json`
          ).catch(handleError);
    case 'tr':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-tr", webpackMode: "lazy" */
            `../../../lang/tr.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-tr", webpackMode: "lazy" */
            `../../../lang/mock/tr.json`
          ).catch(handleError);
    case 'zh-tw':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-zh-tw", webpackMode: "lazy" */
            `../../../lang/zh-tw.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-zh-tw", webpackMode: "lazy" */
            `../../../lang/mock/zh-tw.json`
          ).catch(handleError);

    case 'zh':
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-zh", webpackMode: "lazy" */
            `../../../lang/zh.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-zh", webpackMode: "lazy" */
            `../../../lang/mock/zh.json`
          ).catch(handleError);

    default:
      return process.env.NODE_ENV === 'production'
        ? import(
            /* webpackChunkName: "translated-messages-locale-en", webpackMode: "lazy" */
            `../../../lang/en.json`
          ).catch(handleError)
        : import(
            /* webpackChunkName: "mock-translated-messages-locale-en", webpackMode: "lazy" */
            `../../../lang/mock/en.json`
          ).catch(handleError);
  }
};

// loads the appropriate data for moment and react-intl
export const loadLocaleData = async locale => {
  const lowerCaseLocale = locale;
  const localeBase = locale.split('-')[0];
  const localeCountry = locale.includes('-')
    ? locale.substring(locale.indexOf('-'), locale.length).toLowerCase()
    : '';

  switch (localeBase) {
    case 'cs':
      await import(
        /* webpackChunkName: "moment-locale-cs", webpackMode: "lazy" */
        `moment/locale/cs${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;
    case 'da':
      await import(
        /* webpackChunkName: "moment-locale-da", webpackMode: "lazy" */
        `moment/locale/da${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;
    case 'de':
      await import(
        /* webpackChunkName: "moment-locale-de", webpackMode: "lazy" */
        `moment/locale/de${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;

    case 'en':
      // moment doesn't technically support `en-US`, it loads `en` by default
      if (localeCountry && localeCountry !== '-US') {
        await import(
          /* webpackChunkName: "moment-locale-en", webpackMode: "lazy" */
          `moment/locale/en${localeCountry}`
        )
          .then(() => {
            moment.locale(lowerCaseLocale);
          })
          .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      }

      break;

    case 'es':
      await import(
        /* webpackChunkName: "moment-locale-es", webpackMode: "lazy" */
        `moment/locale/es${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;

    case 'fi':
      await import(
        /* webpackChunkName: "moment-locale-fi", webpackMode: "lazy" */
        `moment/locale/fi${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;

    case 'fr':
      await import(
        /* webpackChunkName: "moment-locale-fr", webpackMode: "lazy" */
        `moment/locale/fr${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;
    case 'hr':
      await import(
        /* webpackChunkName: "moment-locale-hr", webpackMode: "lazy" */
        `moment/locale/hr${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;
    case 'hu':
      await import(
        /* webpackChunkName: "moment-locale-hu", webpackMode: "lazy" */
        `moment/locale/hu${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;

    case 'it':
      await import(
        /* webpackChunkName: "moment-locale-it", webpackMode: "lazy" */
        `moment/locale/it${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;

    case 'ja':
      await import(
        /* webpackChunkName: "moment-locale-ja", webpackMode: "lazy" */
        `moment/locale/ja${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;

    case 'ko':
      await import(
        /* webpackChunkName: "moment-locale-ko", webpackMode: "lazy" */
        `moment/locale/ko${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;

    case 'nl':
      await import(
        /* webpackChunkName: "moment-locale-nl", webpackMode: "lazy" */
        `moment/locale/nl${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;

    case 'nb':
    case 'no':
      await import(
        /* webpackChunkName: "moment-locale-nb", webpackMode: "lazy" */
        `moment/locale/nb${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;

    case 'pl':
      await import(
        /* webpackChunkName: "moment-locale-pl", webpackMode: "lazy" */
        `moment/locale/pl${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;

    case 'pt':
      await import(
        /* webpackChunkName: "moment-locale-pt", webpackMode: "lazy" */
        `moment/locale/pt${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;
    case 'ru':
      await import(
        /* webpackChunkName: "moment-locale-ru", webpackMode: "lazy" */
        `moment/locale/ru${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;
    case 'sk':
      await import(
        /* webpackChunkName: "moment-locale-sk", webpackMode: "lazy" */
        `moment/locale/sk${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;
    case 'sl':
      await import(
        /* webpackChunkName: "moment-locale-sl", webpackMode: "lazy" */
        `moment/locale/sl${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;
    case 'sv':
      await import(
        /* webpackChunkName: "moment-locale-sv", webpackMode: "lazy" */
        `moment/locale/sv${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;
    case 'tr':
      await import(
        /* webpackChunkName: "moment-locale-tr", webpackMode: "lazy" */
        `moment/locale/tr${localeCountry}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;
    case 'zh': // special case because moment doesn't support zh, it uses zh-cn
      await import(
        /* webpackChunkName: "moment-locale-zh", webpackMode: "lazy" */
        `moment/locale/zh${localeCountry.length !== 0 ? localeCountry : '-cn'}`
      )
        .then(() => {
          moment.locale(lowerCaseLocale);
        })
        .catch(noop); // don't need to log this, since we'll fail over to the less specific one
      break;

    default:
      moment.locale('en');

      break;
  }
};

export const getLocaleMessageData = async (
  currentUserLanguages = window.navigator.languages || [
    window.navigator.language || window.navigator.userLanguage || [DEFAULT_LANGUAGE],
  ]
) => {
  const language = getSupportedLanguage(currentUserLanguages);
  const locale = getSupportedLocale(currentUserLanguages);

  await loadLocaleData(locale);

  const { default: messages } = await loadMessages(language);

  return { locale, messages };
};

export const formats = {
  date: {
    dateTime: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hour12: true,
      minute: 'numeric',
    },
    withSecond: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hour12: false,
      minute: 'numeric',
      second: 'numeric',
    },
    missing: 'undefined',
  },

  time: {
    'hour-only': {
      hour: '2-digit',
      hour12: true,
    },
    missing: 'undefined',
  },
};

export const defaultFormats = {
  date: {
    dateTime: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hour12: true,
      minute: 'numeric',
    },
  },
};
